import React, { useState, useEffect, useCallback, useRef } from "react";
import ToastPopup from "./ToastPopup";
import { AddAdminChecklistProps, ControlWeights, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import { apiCall } from "../main/ApiCall";
import "../css/others/Popup.css";
const AddAdminChecklist: React.FC<AddAdminChecklistProps> = ({ newChecklistName, newChecklistDescription, setNewChecklistName, setNewChecklistDescription, handleSaveNewChecklist, onClose }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isIntegratedChecklist, setIsIntegratedChecklist] = useState<string>("Select");
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [selectedNormalControl, setSelectedNormalControl] = useState<string>("");
  const [controlOptions, setControlOptions] = useState<{ value: string; description: string }[]>([]);
  const [loadingControls, setLoadingControls] = useState<boolean>(false);
  const [controlWeights, setControlWeights] = useState<ControlWeights[]>([]);
  const [totalWeights, setTotalWeights] = useState<number>(0);
  const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(false);
  const [prefix, setPrefix] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const extractPrefixFromValue = (scopeValue: string): string => {
    let cleanValue = scopeValue.replace(/\s*Controls\s*/i, '');
    const prefix = cleanValue.trim().split(/\s+/)[0];
    return `${prefix}_`;
  };
  useEffect(() => {
    const fetchControlTypes = async () => {
      setLoadingControls(true);
      try {
        const response = await apiCall("get", "/api/enums/ControlsEnum");
        setControlOptions(response);
      } catch (error) {
        setMessage(createToastMessage("Failed to load control types", "error", 3000));
      } finally {
        setLoadingControls(false);
      }
    };
    fetchControlTypes();
  }, []);
  useEffect(() => {
    if (isIntegratedChecklist === "No") {
      setSelectedControls([]);
      setControlWeights([]);
      setTotalWeights(0);
      setSelectedNormalControl("");
      setPrefix("");
      setNewChecklistName("");
    } else if (isIntegratedChecklist === "Yes") {
      setSelectedNormalControl("");
      setPrefix("");
      setNewChecklistName("");
      setNewChecklistDescription("");
    } else if (isIntegratedChecklist === "Select") {
      setSelectedNormalControl("");
      setPrefix("");
      setNewChecklistName("");
      setNewChecklistDescription("");
      setSelectedControls([]);
      setControlWeights([]);
      setTotalWeights(0);
    }
  }, [isIntegratedChecklist, setNewChecklistName, setNewChecklistDescription]);
  useEffect(() => {
    if (isIntegratedChecklist === "No" && selectedNormalControl) {
      const controlInfo = controlOptions.find(c => c.value === selectedNormalControl);
      const scopeValue = controlInfo?.value || selectedNormalControl;
      const scopePrefix = extractPrefixFromValue(scopeValue);
      setPrefix(scopePrefix);
      setNewChecklistName(scopePrefix);
    } else if (isIntegratedChecklist === "No" && !selectedNormalControl) {
      setNewChecklistName("");
      setPrefix("");
    }
  }, [isIntegratedChecklist, selectedNormalControl, controlOptions, setNewChecklistName]);
  const handleNameChange = (value: string) => {
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option (Yes/No) first", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      setNewChecklistName(value);
    } else if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        setMessage(createToastMessage("Please select a scope first", "warning", 3000));
        return;
      }
      if (value === "") {
        setNewChecklistName(prefix);
      } else if (value.startsWith(prefix)) {
        setNewChecklistName(value);
      } else {
        setNewChecklistName(prefix + value);
        setMessage(createToastMessage("Scope prefix cannot be removed or modified", "warning", 2000));
      }
    }
  };
  const handleDescriptionChange = (value: string) => {
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option (Yes/No) first", "warning", 3000));
      return;
    }
    setNewChecklistDescription(value);
  };
  const calculateTotalWeights = (weights: ControlWeights[]) => {
    const total = weights.reduce((sum, cw) => sum + (cw.weight || 0), 0);
    setTotalWeights(total);
  };
  useEffect(() => {
    setControlWeights((prev) => {
      if (selectedControls.length === 1) {
        setTotalWeights(100);
        return [{ control: selectedControls[0], weight: 100 }];
      }
      if (selectedControls.length > 1) {
        const existing = prev.filter((cw) =>
          selectedControls.includes(cw.control)
        );
        const newWeights = selectedControls.map((control) => {
          const found = existing.find((cw) => cw.control === control);
          return found || { control, weight: 0 };
        });
        calculateTotalWeights(newWeights);
        return newWeights;
      }
      setTotalWeights(0);
      return [];
    });
  }, [selectedControls]);
  const validateForm = useCallback(() => {
    if (!newChecklistDescription.trim()) {
      setIsSaveEnabled(false);
      return;
    }
    if (newChecklistName.length > 60) {
      setIsSaveEnabled(false);
      return;
    }
    if (isIntegratedChecklist === "Select") {
      setIsSaveEnabled(false);
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      if (!newChecklistName.trim()) {
        setIsSaveEnabled(false);
        return;
      }
      if (selectedControls.length <= 1) {
        setIsSaveEnabled(false);
        return;
      }
      if (totalWeights !== 100) {
        setIsSaveEnabled(false);
        return;
      }
      const hasZero = controlWeights.some((cw) => (cw.weight || 0) <= 0);
      if (hasZero) {
        setIsSaveEnabled(false);
        return;
      }
    } else if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        setIsSaveEnabled(false);
        return;
      }
      if (!newChecklistName.startsWith(prefix)) {
        setIsSaveEnabled(false);
        return;
      }
      if (newChecklistName.length <= prefix.length) {
        setIsSaveEnabled(false);
        return;
      }
    }
    setIsSaveEnabled(true);
  }, [newChecklistName, newChecklistDescription, isIntegratedChecklist, selectedControls, totalWeights, controlWeights, selectedNormalControl, prefix]);
  useEffect(() => { validateForm() }, [validateForm]);
  const handleCheckboxChange = (controlValue: string) => {
    setSelectedControls((prev) =>
      prev.includes(controlValue) ? prev.filter((c) => c !== controlValue) : [...prev, controlValue]
    );
  };
  const handleRadioChange = (controlValue: string) => {
    setSelectedNormalControl(controlValue);
  };
  const handleWeightsChange = (control: string, value: string) => {
    if (value === "" || /^\d+$/.test(value)) {
      const numericValue = value === "" ? 0 : Math.max(0, Math.min(100, parseInt(value)));
      setControlWeights((prev) => {
        const updated = prev.map((cw) => cw.control === control ? { ...cw, weight: numericValue } : cw);
        calculateTotalWeights(updated);
        return updated;
      });
    }
  };
  const handleSave = () => {
    const checklistData = { templateName: newChecklistName, description: newChecklistDescription, isIntegratedChecklist: isIntegratedChecklist === "Yes", controls: isIntegratedChecklist === "Yes" ? selectedControls : [], controlWeights: isIntegratedChecklist === "Yes" ? controlWeights.map((cw) => ({ control: cw.control, weight: cw.weight || 0 })) : [], scope: isIntegratedChecklist === "No" ? selectedNormalControl : undefined };
    handleSaveNewChecklist(checklistData);
    setNewChecklistName("");
    setNewChecklistDescription("");
    setIsIntegratedChecklist("Select");
    setSelectedControls([]);
    setControlWeights([]);
    setTotalWeights(0);
    setSelectedNormalControl("");
    setPrefix("");
    onClose();
  };
  const getWeightsClass = () => {
    if (totalWeights === 100) return "weights-ok";
    if (totalWeights > 100) return "weights-over";
    return "weights-pending";
  };
  const restrictedChars = /[\\:*?"<>|/]/;
  const showInvalidCharMessage = () => {
    setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
  };
  const validateRestrictedChars = (value: string) => {
    if (restrictedChars.test(value)) {
      showInvalidCharMessage();
      return false;
    }
    return true;
  };
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      const inputEvent = e as any;
      if (inputEvent.inputType === 'deleteContentBackward' ||
        inputEvent.inputType === 'deleteContentForward' ||
        inputEvent.inputType === 'deleteByCut') {
        if (cursorPosition <= prefix.length) {
          e.preventDefault();
          setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
        }
      }
    }
  };
  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      if (input.selectionStart !== null && input.selectionStart < prefix.length) {
        input.setSelectionRange(prefix.length, input.selectionEnd);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if ((e.key === 'Backspace' && cursorPosition <= prefix.length) ||
        (e.key === 'Delete' && cursorPosition < prefix.length)) {
        e.preventDefault();
        setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
      }
      if (e.key === 'ArrowLeft' && cursorPosition === prefix.length) {
        e.preventDefault();
      }
      if (e.key === 'Home') {
        e.preventDefault();
        input.setSelectionRange(prefix.length, prefix.length);
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < prefix.length) {
        e.preventDefault();
        setMessage(createToastMessage("Cannot paste into the prefix area", "warning", 2000));
      }
    }
  };
  const isNameInputDisabled = () => {
    if (isIntegratedChecklist === "Select") return true;
    if (isIntegratedChecklist === "No" && !selectedNormalControl) return true;
    return false;
  };
  const getNamePlaceholder = () => {
    if (isIntegratedChecklist === "Select") {
      return "Please select Integrated Checklist option first";
    }
    if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        return "Please select a scope first";
      }
      return `Enter name after ${prefix}`;
    }
    return "Enter checklist name";
  };
  return (
    <div className="addchecklist-overlay">
      <div className="addchecklist-content">
        <h3 className="addchecklist-title">Create Checklist</h3>
        <label className="addchecklist-label">Integrated Checklist:
          <select className="addchecklist-select" value={isIntegratedChecklist} onChange={(e) => setIsIntegratedChecklist(e.target.value)}>
            <option value="Select">Select an option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>
        <label className="addchecklist-label">Checklist Name:
          <input ref={inputRef} type="text" value={newChecklistName} onChange={(e) => { const value = e.target.value; if (!validateRestrictedChars(value)) return; handleNameChange(value) }} onKeyDown={handleKeyDown} onSelect={handleSelect} onBeforeInput={handleBeforeInput} onPaste={handlePaste} required className={`addchecklist-input ${isNameInputDisabled() ? 'input-disabled' : ''}`} disabled={isNameInputDisabled()} placeholder={getNamePlaceholder()} />
          {isIntegratedChecklist === "No" && !selectedNormalControl && (
            <small className="checklist-warning">⚠ Please select a scope first to enable the checklist name field</small>
          )}
          {isIntegratedChecklist === "Select" && (
            <small className="checklist-warning">⚠ Please select Yes/No for Integrated Checklist first</small>
          )}
          {isIntegratedChecklist === "No" && selectedNormalControl && newChecklistName.length === prefix.length && (
            <small className="checklist-warning">⚠ Please add a name after the prefix</small>
          )}
        </label>
        <label className="addchecklist-label">Description:
          <textarea value={newChecklistDescription} onChange={(e) => { const value = e.target.value; if (!validateRestrictedChars(value)) return; handleDescriptionChange(value); }} required className={`addchecklist-textarea ${isIntegratedChecklist === "Select" ? "textarea-disabled" : ""}`} placeholder="Enter description" disabled={isIntegratedChecklist === "Select"} />
          {isIntegratedChecklist === "Select" && (
            <small className="checklist-warning">⚠ Please select Yes/No for Integrated Checklist first</small>
          )}
        </label>
        {isIntegratedChecklist !== "Select" && (
          <>
            <label className="addchecklist-subtitle">
              {isIntegratedChecklist === "Yes" ? "Select Controls (Select at least 2):" : "Select Control (Select one):"}</label>
            <div className="addchecklist-controls">
              {loadingControls ? (
                <div className="addchecklist-loading">Loading control types...</div>
              ) : (
                <div className="addchecklist-control-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="addchecklist-control-option">
                      {isIntegratedChecklist === "Yes" ? (
                        <input type="checkbox" value={control.value} checked={selectedControls.includes(control.value)} onChange={() => handleCheckboxChange(control.value)} />
                      ) : (
                        <input type="radio" name="normalControl" value={control.value} checked={selectedNormalControl === control.value} onChange={() => handleRadioChange(control.value)} />
                      )}{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
            {isIntegratedChecklist === "Yes" && selectedControls.length > 0 && (
              <div className="addchecklist-weightage">
                <label className="addchecklist-weightage-label">Control Weights:</label>
                {selectedControls.length === 1 && (
                  <div className="addchecklist-warning">Please select at least two controls for integrated checklist</div>
                )}
                <div className={`addchecklist-total ${getWeightsClass()}`}>Total Weights: {totalWeights}%{" "}{totalWeights !== 100 && "(Must equal 100%)"}</div>
                {selectedControls.length === 1 ? (
                  <div className="addchecklist-single">
                    <p>
                      <strong>{controlOptions.find((c) => c.value === selectedControls[0])?.description}</strong> : 100% (Auto-assigned)
                    </p>
                  </div>
                ) : (
                  <div className="addchecklist-multiple">
                    {selectedControls.map((control) => {
                      const controlInfo = controlOptions.find((c) => c.value === control);
                      const weightsObj = controlWeights.find((cw) => cw.control === control);
                      const weight = weightsObj ? (weightsObj.weight || 0) : 0;
                      return (
                        <div key={control} className="addchecklist-weightage-item">
                          <span className="addchecklist-control-name">{controlInfo?.description} ({control})</span>
                          <div className="addchecklist-input-wrap">
                            <input type="text" inputMode="numeric" pattern="[0-9]*" value={weight} onChange={(e) => handleWeightsChange(control, e.target.value)} className="addchecklist-weightage-input" />
                            <span className="addchecklist-percent">%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}> Cancel </button>
          <button className="savebutton" onClick={handleSave} disabled={!isSaveEnabled}> Save </button>
        </div>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default AddAdminChecklist;